import {
    BuildingBlockType,
    DiagramBuildingBlock,
    DiagramTableBuildingBlock,
    isSerializedDiagramBuildingBlock,
    isSerializedDiagramTableBuildingBlock,
    isSerializedPageBreakBlock,
    isSerializedTableBuildingBlock,
    isSerializedTextBuildingBlock,
    Report,
    SerializedDiagramBuildingBlock,
    SerializedDiagramTableBuildingBlock,
    SerializedReport,
    SerializedTableBuildingBlock,
    TableBuildingBlock,
} from '../types/Report';
import {AnalyticsTab} from '../../stores/store';
import {
    isDiagramInstruction,
    isProductDiagramInstruction,
    isSearchDiagramInstruction,
    ProductDiagramInstruction,
    SearchDiagramInstruction,
} from '../../stores/OdtExportStore';
import {SerializedChannelDiagramRequest, SerializedLineDiagramRequest} from '../../stores/MemoryBarStore';
import {SelectorTableColumnDef} from '../../stores/GeneralSelectorTableStore';

export default class ReportDeserializer {
    constructor(private getTableColumnListWithChannelPerAnalyticsTab: (analyticsTab: AnalyticsTab) => SelectorTableColumnDef[]) {}

    public deserializeReports(reports: SerializedReport[]): Report[] {
        return reports.map((report) => ({
            ...report,
            blocks: report.blocks.map((reportBuildingBlock) => {
                if (isSerializedDiagramBuildingBlock(reportBuildingBlock)) {
                    return ReportDeserializer.deserializeDiagramBuildingBlock(reportBuildingBlock);
                } else if (isSerializedDiagramTableBuildingBlock(reportBuildingBlock)) {
                    return this.deserializeDiagramTableBuildingBlock(reportBuildingBlock);
                } else if (isSerializedTableBuildingBlock(reportBuildingBlock)) {
                    return this.deserializeTableBuildingBlock(reportBuildingBlock);
                } else if (isSerializedTextBuildingBlock(reportBuildingBlock)) {
                    return reportBuildingBlock;
                } else if (isSerializedPageBreakBlock(reportBuildingBlock)) {
                    return reportBuildingBlock;
                }
                throw Error('Deserialization of block type ' + reportBuildingBlock.blockType + ' not implemented yet');
            }),
        }));
    }

    private static deserializeDiagramBuildingBlock(diagramBuildingBlock: SerializedDiagramBuildingBlock): DiagramBuildingBlock {
        const {channelDiagrams, diagrams} = ReportDeserializer.splitDiagramInstructionsToChannelAndNormal(
            diagramBuildingBlock.searchDiagrams.length > 0 ? diagramBuildingBlock.searchDiagrams : diagramBuildingBlock.productDiagrams
        );

        const {idDateCompare, allowDateCompareRange} = ((): {allowDateCompareRange: boolean; idDateCompare: {idDateCompare?: string}} => {
            if (diagramBuildingBlock.idDateCompare) {
                return {
                    idDateCompare: {idDateCompare: diagramBuildingBlock.idDateCompare},
                    allowDateCompareRange: true,
                };
            }
            return {idDateCompare: {}, allowDateCompareRange: false};
        })();
        return {
            id: diagramBuildingBlock.id,
            blockType: BuildingBlockType.DIAGRAM,
            idDate: diagramBuildingBlock.idDate,
            ...idDateCompare,
            memoryItems: [
                {
                    allowDateCompareRange,
                    analyticsTab: diagramBuildingBlock.searchDiagrams.length > 0 ? AnalyticsTab.SearchView : AnalyticsTab.ProductView,
                    activeDiagramItems: {
                        channelDiagrams,
                        diagrams,
                        showAbsoluteValues: false,
                    },
                },
            ],
        };
    }

    private static splitDiagramInstructionsToChannelAndNormal(
        diagramInstructions: SearchDiagramInstruction[] | ProductDiagramInstruction[]
    ): {diagrams: SerializedLineDiagramRequest[]; channelDiagrams: SerializedChannelDiagramRequest[]} {
        const normalDiagramInstructions: SerializedLineDiagramRequest[] = [];
        const channelDiagramInstructions: SerializedChannelDiagramRequest[] = [];

        const diagramInstructionToLoop = diagramInstructions as any[];
        diagramInstructionToLoop.forEach((diagramInstruction) => {
            if (isSearchDiagramInstruction(diagramInstruction)) {
                if (diagramInstruction.searchterm) {
                    normalDiagramInstructions.push({
                        kpi: diagramInstruction.kpi,
                        channel: diagramInstruction.channel,
                        searchtermOrProductId: diagramInstruction.searchterm,
                    });
                } else {
                    channelDiagramInstructions.push(diagramInstruction);
                }
            } else if (isProductDiagramInstruction(diagramInstruction)) {
                if (diagramInstruction.productId) {
                    normalDiagramInstructions.push({
                        searchtermOrProductId: diagramInstruction.productId,
                        channel: diagramInstruction.channel,
                        kpi: diagramInstruction.kpi,
                    });
                } else {
                    channelDiagramInstructions.push(diagramInstruction);
                }
            } else if (isDiagramInstruction(diagramInstruction)) {
                channelDiagramInstructions.push(diagramInstruction);
            } else {
                throw Error('Unknown diagram instruction type');
            }
        });

        return {diagrams: normalDiagramInstructions, channelDiagrams: channelDiagramInstructions};
    }

    private deserializeDiagramTableBuildingBlock(diagramTableBuildingBlock: SerializedDiagramTableBuildingBlock): DiagramTableBuildingBlock {
        const allColumns = this.getTableColumnListWithChannelPerAnalyticsTab(
            diagramTableBuildingBlock.searchDiagrams.length > 0 ? AnalyticsTab.SearchView : AnalyticsTab.ProductView
        );
        const {channelDiagrams, diagrams} = ReportDeserializer.splitDiagramInstructionsToChannelAndNormal(
            diagramTableBuildingBlock.searchDiagrams.length > 0 ? diagramTableBuildingBlock.searchDiagrams : diagramTableBuildingBlock.productDiagrams
        );

        const {idDateCompare, allowDateCompareRange} = ((): {allowDateCompareRange: boolean; idDateCompare: {idDateCompare?: string}} => {
            if (diagramTableBuildingBlock.idDateCompare) {
                return {
                    idDateCompare: {idDateCompare: diagramTableBuildingBlock.idDateCompare},
                    allowDateCompareRange: true,
                };
            }
            return {idDateCompare: {}, allowDateCompareRange: false};
        })();

        return {
            id: diagramTableBuildingBlock.id,
            blockType: BuildingBlockType.DIAGRAM_TABLE,
            idDate: diagramTableBuildingBlock.idDate,
            ...idDateCompare,
            memoryItems: [
                {
                    allowDateCompareRange,
                    analyticsTab: diagramTableBuildingBlock.searchDiagrams.length > 0 ? AnalyticsTab.SearchView : AnalyticsTab.ProductView,
                    activeDiagramItems: {
                        channelDiagrams,
                        diagrams,
                        showAbsoluteValues: false,
                    },
                },
            ],
            columns: allColumns.map((column) => ({
                ...column,
                isVisible: diagramTableBuildingBlock.columns.some((savedColumn) => (savedColumn as any) === column.dataKey),
            })),
        };
    }

    private deserializeTableBuildingBlock(tableBuildingBlock: SerializedTableBuildingBlock): TableBuildingBlock {
        const analyticsTab = tableBuildingBlock.tableMode === 'search' ? AnalyticsTab.SearchView : AnalyticsTab.ProductView;
        const allColumns = this.getTableColumnListWithChannelPerAnalyticsTab(analyticsTab);

        return {
            id: tableBuildingBlock.id,
            blockType: BuildingBlockType.TABLE,
            lineLimit: tableBuildingBlock.limit,
            idDate: tableBuildingBlock.idDate,
            columns: allColumns.map((column) => ({
                ...column,
                isVisible: tableBuildingBlock.columns.some((savedColumn) => (savedColumn as any) === column.dataKey),
            })),
            memoryItems: [
                {
                    analyticsTab,
                    activeSearch: tableBuildingBlock.search,
                    activeTableSort: {
                        sortDirection: tableBuildingBlock.sortDirection,
                        attribute: tableBuildingBlock.sortAttribute,
                    },
                    activeFilters: tableBuildingBlock.filters,
                    channel: tableBuildingBlock.channel,
                },
            ],
        };
    }
}



// WEBPACK FOOTER //
// ./src/report/utility/ReportDeserializer.ts